import { useCallback, useState } from 'react';
import { useInjection } from 'inversify-react';
import { API } from '../services/HTTP';
import { TimeSeriesResponse, toTimeSeriesChartRows, type Tile } from '../components/TileDisplay';
import type { TileLayout } from './useTilePersistence';

/**
 * Hook for fetching and managing tile data (separate from layout).
 * Tiles are configurations; this hook fills in the actual data.
 */
export function useTileData() {
  const api = useInjection<API>('API');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch data for a set of tile layouts.
   * Queries the timestream API for each datasource and merges with layout info.
   */
  const fetchTileData = useCallback(
    async (layouts: TileLayout[], dateRange?: { start?: number; end?: number }): Promise<Tile[]> => {
      setLoading(true);
      setError(null);

      const safeLayouts = Array.isArray(layouts) ? layouts : [];

      try {
        const params = {
          ...(dateRange?.start && { start: dateRange.start }),
          ...(dateRange?.end && { end: dateRange.end }),
        };

        const tilesWithData = await Promise.all(
          safeLayouts.map(async (layout) => {
            const response = await api.get('timestream', { params });

            if (response.status >= 400) {
              return { ...layout, data: [] };
            }

            const payload = (typeof response.data === 'string'
              ? JSON.parse(response.data)
              : response.data) as TimeSeriesResponse;

            return { ...layout, data: toTimeSeriesChartRows(payload.rows) };
          })
        );

        return tilesWithData;
      } catch (err) {
        console.error('Error fetching tile data:', err);
        setError('Failed to fetch data. Please try again later.');
        return safeLayouts.map((layout) => ({ ...layout, data: [] }));
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  return { fetchTileData, loading, error };
}
